/**
 * PricingPage Component
 *
 * This pricing page is used exclusively for NEW customers.
 * Existing/old customers are directed to the legacy pricing page at SetPlan.js
 * which maintains the original pricing structure and functionality.
 */

import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Grid, Box, Alert, CircularProgress } from '@mui/material';
import { StyledSnackbar } from '../../common/AppSnackbar';
import { loadStripe } from '@stripe/stripe-js';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import CONFIG from '../../../config';
import DowngradeDialog from './DowngradeDialog';
import PlanCard from './PlanCard';
import { getAllPlans, getPlan, PLAN_NAMES } from './plansConfig';
import { getCurrentPlanConfig } from './currentPlanConfig';
import * as billingApi from './billingApi';
import { send_request } from '../../../utils/Request';

const publishableKey = CONFIG.PUBLISHABLEKEY_V2;
const ANIMATION_DURATION = 300;

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: theme.palette.background.subtle,
  padding: '24px 48px',
  [theme.breakpoints.up('md')]: {
    padding: '48px 80px',
  },
}));

const HeaderSection = styled('header')({
  textAlign: 'center',
  marginBottom: 32,
});

const BackButton = styled(Link)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  textDecoration: 'none',
  color: theme.palette.text.secondary,
  fontSize: '14px',
  fontWeight: 500,
  marginTop: 32,
  marginBottom: 8,
  transition: 'color 0.2s ease',
  '&:hover': {
    color: theme.palette.text.primary,
  },
  '& .MuiSvgIcon-root': {
    marginRight: 8,
    fontSize: '20px',
  },
}));

const MainTitle = styled('h1')(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 600,
  margin: '0 0 24px 0',
  fontSize: '2.25rem',
  letterSpacing: '-0.025em',
  lineHeight: 1.2,
  '@media (min-width: 768px)': {
    fontSize: '2rem',
  },
}));

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '400px',
});

const PricingPage = () => {
  // URL search params for detecting successful payment returns
  const [searchParams, setSearchParams] = useSearchParams();

  // State management
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // UI state derived from subscription data
  const [addOnQuantities, setAddOnQuantities] = useState({ actions: 0, aiCredits: 0 });
  const [animations, setAnimations] = useState({ actions: null, aiCredits: null });
  const [isEditing, setIsEditing] = useState(false);
  const [originalQuantities, setOriginalQuantities] = useState({ actions: 0, aiCredits: 0 });
  const [pendingChanges, setPendingChanges] = useState({ plan: null, addOns: null });
  const [showPendingChanges, setShowPendingChanges] = useState(false);
  const [downgradeDialogOpen, setDowngradeDialogOpen] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(PLAN_NAMES.FREE);
  const [nextBillingDate, setNextBillingDate] = useState(null);

  // Load subscription data on component mount
  useEffect(() => {
    const processCheckoutAndLoadData = async () => {
      // Check if we have a session_id to process from successful payment (Stripe direct redirect)
      const sessionId = searchParams.get('session_id');

      if (sessionId) {
        try {
          console.log('Processing checkout for session:', sessionId);

          // Process checkout completion in backend
          const response = await send_request(
            'authz-service/v2/billing/checkout/success',
            { sessionId },
            null,
            'POST'
          );

          if (response.status === 200) {
            console.log('Checkout processed successfully');
            showSnackbar('🎉 Payment successful! Your plan has been updated.', 'success');
          } else {
            console.error(`Backend returned ${response.status} for checkout processing`);
            showSnackbar(
              'Payment was successful, but there was an issue updating your subscription. Please refresh the page.',
              'warning'
            );
          }
        } catch (err) {
          console.error('Error processing checkout:', err);
          showSnackbar(
            'Payment was successful, but there was an issue updating your subscription. Please refresh the page.',
            'warning'
          );
        }

        // Clean up URL parameters
        searchParams.delete('session_id');
        searchParams.delete('from_success');
        setSearchParams(searchParams);
      }
    };

    // Process checkout first if needed, then load subscription data
    processCheckoutAndLoadData().then(() => {
      loadSubscriptionData();
    });

    // Check if user is returning from cancelled payment (direct from Stripe)
    const fromCancel = searchParams.get('from_cancel');
    if (fromCancel === 'true') {
      // Redirect to pricing page without the from_cancel parameter
      searchParams.delete('from_cancel');
      setSearchParams(searchParams, { replace: true });
    }

    // Handle legacy from_success parameter (in case it comes without session_id)
    const fromSuccess = searchParams.get('from_success');
    if (fromSuccess === 'true' && !searchParams.get('session_id')) {
      showSnackbar('🎉 Payment successful! Your plan has been updated.', 'success');
      searchParams.delete('from_success');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await billingApi.getCurrentSubscription();
      setSubscriptionData(data);

      // Update UI state from backend data
      setCurrentPlan(data.currentPlan);
      setAddOnQuantities(data.addOnQuantities);
      setOriginalQuantities(data.originalQuantities);
      setPendingChanges(data.pendingChanges);
      setShowPendingChanges(data.showPendingChanges);
      setNextBillingDate(data.nextBillingDate);
    } catch (err) {
      console.error('Failed to load subscription data:', err);

      // Handle specific V2 subscription errors
      if (err.message && err.message.includes('No V2 subscription found')) {
        setError('Setting up your V2 billing account... Please refresh the page in a moment.');
        showSnackbar('Creating your subscription account...', 'info');
      } else {
        setError('Failed to load subscription data. Please refresh the page.');
      }

      // Fallback to mock data
      const fallbackConfig = getCurrentPlanConfig();
      setCurrentPlan(fallbackConfig.currentPlan);
      setAddOnQuantities(fallbackConfig.addOnQuantities);
      setOriginalQuantities(fallbackConfig.originalQuantities);
      setPendingChanges(fallbackConfig.pendingChanges);
      setShowPendingChanges(fallbackConfig.showPendingChanges);
      setNextBillingDate(fallbackConfig.nextBillingDate);
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const resetAnimation = addOnType => {
    setTimeout(() => {
      setAnimations(prev => ({
        ...prev,
        [addOnType]: null,
      }));
    }, ANIMATION_DURATION);
  };

  const handleQuantityChange = (addOnType, change) => {
    setAddOnQuantities(prev => ({
      ...prev,
      [addOnType]: Math.max(0, prev[addOnType] + change),
    }));

    const animationType = change > 0 ? 'up' : 'down';
    setAnimations(prev => ({
      ...prev,
      [addOnType]: animationType,
    }));

    resetAnimation(addOnType);
  };

  const handleQuantityInput = (addOnType, value) => {
    const newQuantity = Math.max(0, parseInt(value) || 0);
    const currentQuantity = addOnQuantities[addOnType];
    const change = newQuantity - currentQuantity;

    setAddOnQuantities(prev => ({
      ...prev,
      [addOnType]: newQuantity,
    }));

    if (change !== 0) {
      const animationType = change > 0 ? 'up' : change < 0 ? 'down' : null;
      setAnimations(prev => ({
        ...prev,
        [addOnType]: animationType,
      }));

      resetAnimation(addOnType);
    }
  };

  const handleEditMode = () => {
    setIsEditing(true);
    setOriginalQuantities({ ...addOnQuantities });
  };

  const hasReductions = (original, current) => {
    return current.actions < original.actions || current.aiCredits < original.aiCredits;
  };

  const hasUpgrades = (original, current) => {
    return current.actions > original.actions || current.aiCredits > original.aiCredits;
  };

  const getSuccessUrl = () => {
    return `${window.location.origin}/pricing?session_id={CHECKOUT_SESSION_ID}&from_success=true`;
  };

  const getCancelUrl = () => {
    return `${window.location.origin}/pricing?from_cancel=true`;
  };

  const handleSaveChanges = async () => {
    try {
      setProcessing(true);
      const reductionsDetected = hasReductions(originalQuantities, addOnQuantities);
      const upgradesDetected = hasUpgrades(originalQuantities, addOnQuantities);

      // If there are upgrades that require payment, use checkout flow
      if (upgradesDetected) {
        // Create checkout session for addon updates using bulk endpoint
        const addonUpdates = [];

        if (addOnQuantities.actions !== originalQuantities.actions) {
          addonUpdates.push({
            type: 'actions', // Frontend type, will be mapped in billingApi
            quantity: addOnQuantities.actions,
          });
        }

        if (addOnQuantities.aiCredits !== originalQuantities.aiCredits) {
          addonUpdates.push({
            type: 'aiCredits', // Frontend type, will be mapped in billingApi
            quantity: addOnQuantities.aiCredits,
          });
        }

        // Use the existing billingApi function for bulk addon checkout
        const checkoutData = await billingApi.createAddonCheckoutSession(
          addonUpdates,
          getSuccessUrl(),
          getCancelUrl()
        );

        // Check if this was a direct addon update (no payment required)
        if (checkoutData.sessionId === 'bulk_addons_added_directly' && !checkoutData.url) {
          // Addons were added directly, show success message
          showSnackbar('🎉 Add-ons updated successfully!', 'success');

          // Reload subscription data to get updated state
          await loadSubscriptionData();
          setIsEditing(false);
          setProcessing(false);
          return;
        }

        // Otherwise, proceed with Stripe checkout
        const stripePromise = loadStripe(publishableKey);
        const stripe = await stripePromise;

        stripe.redirectToCheckout({
          sessionId: checkoutData.sessionId,
        });
        return;
      }

      // For downgrades only, use direct API calls (scheduled for next billing cycle)
      const promises = [];

      if (addOnQuantities.actions !== originalQuantities.actions) {
        promises.push(
          billingApi.updateAddon('actions', addOnQuantities.actions, !reductionsDetected)
        );
      }

      if (addOnQuantities.aiCredits !== originalQuantities.aiCredits) {
        promises.push(
          billingApi.updateAddon('aiCredits', addOnQuantities.aiCredits, !reductionsDetected)
        );
      }

      if (promises.length > 0) {
        await Promise.all(promises);

        // Reload subscription data to get updated state
        await loadSubscriptionData();

        if (reductionsDetected) {
          showSnackbar('Changes scheduled for next billing cycle', 'info');
        } else {
          showSnackbar('Plan updated successfully');
        }
      }

      setIsEditing(false);
    } catch (err) {
      console.error('Failed to save changes:', err);
      showSnackbar('Failed to save changes. Please try again.', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelChanges = () => {
    setIsEditing(false);
    setAddOnQuantities({ ...originalQuantities });
    setAnimations({ actions: null, aiCredits: null });
  };

  const handleOpenDowngradeDialog = () => {
    setDowngradeDialogOpen(true);
  };

  const handleCloseDowngradeDialog = () => {
    setDowngradeDialogOpen(false);
  };

  const handleConfirmDowngrade = async () => {
    try {
      setProcessing(true);
      setDowngradeDialogOpen(false);

      if (currentPlan === PLAN_NAMES.PRO) {
        // Downgrade from Professional to Free - this will be scheduled for next billing cycle
        await billingApi.cancelSubscription();
        await loadSubscriptionData();
        showSnackbar('Downgrade scheduled for next billing cycle', 'info');
      }
    } catch (err) {
      console.error('Failed to downgrade:', err);
      showSnackbar('Failed to process downgrade. Please try again.', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpgrade = async newPlan => {
    try {
      console.log('handleUpgrade called with:', { newPlan, currentPlan });
      setProcessing(true);

      if (currentPlan === PLAN_NAMES.FREE && newPlan === PLAN_NAMES.PRO) {
        console.log('Attempting to upgrade from Free to Professional using checkout...');

        // Create checkout session for plan upgrade using V2 endpoint
        const requestBody = {
          newPlan: 'BASE_PLAN', // Backend expects 'BASE_PLAN' for Professional
          successUrl: getSuccessUrl(),
          cancelUrl: getCancelUrl(),
        };

        const json = await send_request(
          'authz-service/v2/billing/checkout/plan-change',
          requestBody,
          '',
          'POST'
        );

        if (json && json.status === 200) {
          const stripePromise = loadStripe(publishableKey);
          const stripe = await stripePromise;

          stripe.redirectToCheckout({
            sessionId: json.data.sessionId,
          });
        } else {
          throw new Error('Failed to create checkout session');
        }
      } else {
        console.log('Upgrade conditions not met:', { currentPlan, newPlan });
        showSnackbar('Invalid upgrade path', 'error');
        setProcessing(false);
      }
    } catch (err) {
      console.error('Failed to upgrade:', err);
      showSnackbar('Failed to process upgrade. Please try again.', 'error');
      setProcessing(false);
    }
  };

  const handleCancelPendingChanges = async () => {
    try {
      setProcessing(true);

      // Cancel all pending updates
      if (subscriptionData?.pendingUpdates) {
        const cancelPromises = subscriptionData.pendingUpdates
          .filter(update => update.userCancellable)
          .map(update => billingApi.cancelPendingUpdate(update.id));

        await Promise.all(cancelPromises);
      }

      // Reload subscription data
      await loadSubscriptionData();
      showSnackbar('Pending changes cancelled successfully');
    } catch (err) {
      console.error('Failed to cancel pending changes:', err);
      showSnackbar('Failed to cancel pending changes. Please try again.', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const hasPendingChanges = () => {
    return (
      pendingChanges &&
      (pendingChanges.plan ||
        (pendingChanges.addOns &&
          (pendingChanges.addOns.actions !== null || pendingChanges.addOns.aiCredits !== null)))
    );
  };

  const createPlanButtonConfig = (plan, isCurrent) => {
    if (plan.name === PLAN_NAMES.FREE) {
      return isCurrent
        ? { buttonText: null, isDowngrade: false }
        : { buttonText: 'Downgrade', isDowngrade: true };
    }

    if (plan.name === PLAN_NAMES.PRO && !isCurrent) {
      if (currentPlan === PLAN_NAMES.FREE) {
        return { buttonText: 'Upgrade', isDowngrade: false };
      }
      if (currentPlan === PLAN_NAMES.ENTERPRISE) {
        return { buttonText: 'Downgrade', isDowngrade: true };
      }
    }

    if (plan.name === PLAN_NAMES.ENTERPRISE && !isCurrent) {
      return { buttonText: 'Talk to Sales', isDowngrade: false };
    }

    return { buttonText: null, isDowngrade: false };
  };

  const buildPlansData = () => {
    const allPlans = getAllPlans();

    // Filter out Professional tier (deprecated for new/free users)
    const filteredPlans = allPlans.filter(plan => plan.name !== PLAN_NAMES.PRO);

    return filteredPlans.map(plan => {
      const isCurrent = plan.name === currentPlan;
      const buttonConfig = createPlanButtonConfig(plan, isCurrent);

      const updatedPlan = {
        ...plan,
        isCurrent,
        ...buttonConfig,
      };

      return updatedPlan;
    });
  };

  // Show loading state
  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <CircularProgress size={40} />
        </LoadingContainer>
      </PageContainer>
    );
  }

  const plans = buildPlansData();

  return (
    <PageContainer>
      <Box maxWidth="1000px" mx="auto">
        <Box textAlign="center" mb={4}>
          <HeaderSection>
            <BackButton to="/organization">
              <ArrowBack />
              Back to Organization Settings
            </BackButton>
            <MainTitle>Subscription and Plans</MainTitle>
          </HeaderSection>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={4} justifyContent="center">
          {plans.map(plan => (
            <Grid item xs={12} md={6} key={plan.name}>
              <PlanCard
                plan={plan}
                isEditing={isEditing}
                hasPendingChanges={hasPendingChanges()}
                pendingChanges={pendingChanges}
                currentPlan={currentPlan}
                originalQuantities={originalQuantities}
                nextBillingDate={nextBillingDate}
                onOpenDowngradeDialog={handleOpenDowngradeDialog}
                onEditMode={handleEditMode}
                onSaveChanges={handleSaveChanges}
                onCancelChanges={handleCancelChanges}
                onCancelPendingChanges={handleCancelPendingChanges}
                processing={processing}
                onUpgrade={handleUpgrade}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      <DowngradeDialog
        open={downgradeDialogOpen}
        onClose={handleCloseDowngradeDialog}
        onConfirm={handleConfirmDowngrade}
        currentPlan={getPlan(currentPlan)}
        targetPlan={getPlan(PLAN_NAMES.FREE)}
        processing={processing}
      />

      <StyledSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        autoHideDuration={6000}
      />
    </PageContainer>
  );
};

export default PricingPage;
